import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell, ReferenceLine,
} from "recharts"
import * as XLSX from "xlsx"
import { setBreadcrumbItems } from "../../../store/actions"
import { listarPresupuestos, obtenerPresupuesto, getVentasReales } from "../api"
import {
  formatCurrency, formatNumber, formatPercent, formatFecha,
  estadoConfig, periodicidadLabel, ANIOS_DISPONIBLES,
  muestraMonto, muestraUnidades,
  cumplimientoConfig, capPct,
} from "../helpers"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Presupuestos", link: "#" },
  { title: "Comparativo vs Ventas", link: "#" },
]

const ahora = new Date()

const ComparativoVentas = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [anio, setAnio] = useState(ahora.getFullYear())
  const [presupuestos, setPresupuestos] = useState([])
  const [loadingLista, setLoadingLista] = useState(false)
  const [presupuestoId, setPresupuestoId] = useState("")

  const [detalle, setDetalle] = useState(null)
  const [loadingDetalle, setLoadingDetalle] = useState(false)
  const [error, setError] = useState("")

  // Map periodoId → { monto_real, documentos_real, loading, error }
  const [ventasPorPeriodo, setVentasPorPeriodo] = useState({})
  const [cargandoVentas, setCargandoVentas] = useState(false)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaId = empresaSeleccionada?.id || empresaSeleccionada?.idempresa || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Comparativo de Ventas | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Comparativo de Ventas"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setPresupuestos([])
    setPresupuestoId("")
    setDetalle(null)
    setVentasPorPeriodo({})
    setError("")
  }, [empresaId])

  // Cargar lista de presupuestos APROBADOS+ACTIVOS+CERRADOS del año (los que tienen sentido comparar)
  const cargarLista = useCallback(async () => {
    if (!empresaId) return
    setLoadingLista(true)
    try {
      const r = await listarPresupuestos({ empresaId, anio })
      const body = r.data
      const data = body?.data || []
      // Filtramos los útiles para comparativa (excluimos BORRADOR/CANCELADO/eliminados)
      const utiles = data.filter(p =>
        ["APROBADO", "ACTIVO", "CERRADO"].includes(p.estado),
      )
      setPresupuestos(utiles)
      // Auto-seleccionar el activo si hay uno solo
      const activo = utiles.find(p => p.estado === "ACTIVO")
      if (activo) setPresupuestoId(activo.id)
      else if (utiles.length === 1) setPresupuestoId(utiles[0].id)
      else setPresupuestoId("")
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar la lista de presupuestos.")
    } finally {
      setLoadingLista(false)
    }
  }, [empresaId, anio])

  useEffect(() => { cargarLista() }, [cargarLista])

  // Cargar detalle del presupuesto seleccionado
  const cargarDetalle = useCallback(async (id) => {
    if (!id) { setDetalle(null); return }
    setLoadingDetalle(true)
    setError("")
    setVentasPorPeriodo({})
    try {
      const r = await obtenerPresupuesto(id)
      const body = r.data
      if (body?.success === false) throw new Error(body.message || "Error en el servidor")
      setDetalle(body?.data || body)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar el presupuesto.")
      setDetalle(null)
    } finally {
      setLoadingDetalle(false)
    }
  }, [])

  useEffect(() => { cargarDetalle(presupuestoId) }, [presupuestoId, cargarDetalle])

  // Trae ventas reales para cada periodo en paralelo
  const cargarVentasReales = useCallback(async () => {
    if (!detalle?.periodos?.length || !nitemisor) return
    setCargandoVentas(true)
    setError("")

    // Inicializar todos los periodos como "cargando"
    const initial = {}
    detalle.periodos.forEach(p => { initial[p.id] = { loading: true } })
    setVentasPorPeriodo(initial)

    const sucursal = detalle.sucursal?.nombre || ""

    const promesas = detalle.periodos.map(async (p) => {
      try {
        const fi = String(p.fechaInicio).substring(0, 10)
        const ff = String(p.fechaFin).substring(0, 10)
        const r = await getVentasReales({
          nitemisor,
          fecha_inicio: fi,
          fecha_fin: ff,
          sucursal: sucursal || undefined,
        })
        // El back ahora devuelve kpis_principales.facturacion_neta y .documentos
        const body = r.data
        const kpis = body?.kpis_principales || body?.data?.kpis_principales || {}
        const monto = kpis.facturacion_neta ?? body?.resumen_ejecutivo?.facturacion_total ?? 0
        const documentos = kpis.documentos ?? body?.resumen_ejecutivo?.total_documentos ?? 0
        return { id: p.id, monto: Number(monto) || 0, documentos: Number(documentos) || 0 }
      } catch (err) {
        return { id: p.id, error: err.response?.data?.message || err.message || "Error" }
      }
    })

    const resultados = await Promise.all(promesas)

    const nuevo = {}
    resultados.forEach(r => {
      if (r.error) {
        nuevo[r.id] = { loading: false, error: r.error }
      } else {
        nuevo[r.id] = { loading: false, monto: r.monto, documentos: r.documentos }
      }
    })
    setVentasPorPeriodo(nuevo)
    setCargandoVentas(false)
  }, [detalle, nitemisor])

  // ──────── Derivados ────────

  const tipoMedicion = detalle?.tipoMedicion || "MONTO"
  const sucursalNombre = detalle?.sucursal?.nombre || "Nivel empresa"

  // Datos por periodo enriquecidos con cumplimiento
  const filasComparativo = useMemo(() => {
    if (!detalle?.periodos) return []
    return detalle.periodos.map(p => {
      const vp = ventasPorPeriodo[p.id] || {}
      const objetivoMonto = parseFloat(p.montoObjetivo) || 0
      const objetivoUnidades = parseFloat(p.unidadesObjetivo) || 0
      const realMonto = vp.monto != null ? Number(vp.monto) : null
      const realDocumentos = vp.documentos != null ? Number(vp.documentos) : null
      const cumplMonto = (realMonto != null && objetivoMonto > 0)
        ? (realMonto / objetivoMonto) * 100
        : null
      return {
        periodo: p,
        objetivoMonto,
        objetivoUnidades,
        realMonto,
        realDocumentos,
        cumplMonto,
        loading: vp.loading,
        error: vp.error,
      }
    })
  }, [detalle, ventasPorPeriodo])

  // KPIs globales
  const totales = useMemo(() => {
    if (filasComparativo.length === 0) return null
    const objetivo = filasComparativo.reduce((a, f) => a + f.objetivoMonto, 0)
    const real = filasComparativo.reduce((a, f) => a + (f.realMonto || 0), 0)
    const documentos = filasComparativo.reduce((a, f) => a + (f.realDocumentos || 0), 0)
    const cumplimiento = objetivo > 0 ? (real / objetivo) * 100 : 0
    const conReal = filasComparativo.filter(f => f.realMonto != null).length
    const periodosCumplidos = filasComparativo.filter(f => (f.cumplMonto || 0) >= 95).length
    const periodosEnRiesgo = filasComparativo.filter(f => f.cumplMonto != null && f.cumplMonto >= 80 && f.cumplMonto < 95).length
    const periodosBajos = filasComparativo.filter(f => f.cumplMonto != null && f.cumplMonto < 80).length
    return { objetivo, real, documentos, cumplimiento, conReal, periodosCumplidos, periodosEnRiesgo, periodosBajos }
  }, [filasComparativo])

  // Data para el chart de tendencia (objetivo vs real)
  const chartData = useMemo(() => {
    return filasComparativo.map(f => ({
      etiqueta: f.periodo.etiqueta,
      objetivo: f.objetivoMonto,
      real: f.realMonto,
      cumplimientoPct: f.cumplMonto,
    }))
  }, [filasComparativo])

  const moneda = detalle?.moneda || "COP"

  // ──────── Export Excel ────────

  const exportarExcel = () => {
    if (!detalle || filasComparativo.length === 0) return

    const headerCabecera = [
      ["COMPARATIVO PRESUPUESTO VS VENTAS REALES"],
      [],
      ["Empresa:", empresaNombre],
      ["NIT:", nitemisor],
      ["Presupuesto:", detalle.nombre],
      ["Versión:", `v${detalle.version}`],
      ["Estado:", detalle.estado],
      ["Vigencia:", `${formatFecha(detalle.fechaInicio)} — ${formatFecha(detalle.fechaFin)}`],
      ["Periodicidad:", periodicidadLabel(detalle.periodicidad)],
      ["Alcance:", sucursalNombre],
      ["Moneda:", moneda],
      ["Generado:", new Date().toLocaleString("es-CO")],
      [],
      ["RESUMEN GLOBAL"],
      ["Objetivo total:", totales?.objetivo || 0],
      ["Real total:", totales?.real || 0],
      ["% Cumplimiento global:", totales?.cumplimiento != null ? `${totales.cumplimiento.toFixed(2)}%` : "—"],
      ["Documentos totales:", totales?.documentos || 0],
      ["Periodos cumplidos (≥95%):", totales?.periodosCumplidos ?? 0],
      ["Periodos en riesgo (80-95%):", totales?.periodosEnRiesgo ?? 0],
      ["Periodos bajos (<80%):", totales?.periodosBajos ?? 0],
      [],
      ["DETALLE POR PERIODO"],
      ["#", "Etiqueta", "Desde", "Hasta", "Objetivo", "Real", "% Cumplimiento", "Documentos reales", "Estado"],
    ]

    const filas = filasComparativo.map(f => {
      const cfg = cumplimientoConfig(f.cumplMonto)
      return [
        f.periodo.numeroPeriodo,
        f.periodo.etiqueta,
        formatFecha(f.periodo.fechaInicio),
        formatFecha(f.periodo.fechaFin),
        f.objetivoMonto,
        f.realMonto ?? "",
        f.cumplMonto != null ? `${f.cumplMonto.toFixed(2)}%` : "—",
        f.realDocumentos ?? "",
        cfg.label,
      ]
    })

    const data = [...headerCabecera, ...filas]
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(data)
    ws["!cols"] = [
      { wch: 4 }, { wch: 28 }, { wch: 12 }, { wch: 12 },
      { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 12 },
    ]
    XLSX.utils.book_append_sheet(wb, ws, "Comparativo")
    const nombre = `Comparativo_${detalle.nombre.replace(/[^a-zA-Z0-9]+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`
    XLSX.writeFile(wb, nombre)
  }

  if (!empresaId) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> arriba a la derecha.
        </p>
      </div>
    )
  }

  const estadoCfg = detalle ? estadoConfig(detalle.estado) : null
  const presupuestoSeleccionado = presupuestos.find(p => p.id === presupuestoId)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Presupuestos
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Comparativo vs Ventas Reales
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong>{nitemisor && ` · NIT ${nitemisor}`}
            </div>
          </div>
          {detalle && totales && (
            <button type="button" className="btn btn-outline-success"
              onClick={exportarExcel}
              disabled={cargandoVentas || totales.conReal === 0}>
              <i className="mdi mdi-microsoft-excel me-1" />
              Exportar Excel
            </button>
          )}
        </div>

        {/* Selector de presupuesto */}
        <div className="d-flex flex-wrap align-items-end gap-2" style={{ gap: 8 }}>
          <div>
            <div style={labelStyle}>Año</div>
            <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={input}>
              {ANIOS_DISPONIBLES.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={labelStyle}>Presupuesto a comparar</div>
            <select value={presupuestoId} onChange={e => setPresupuestoId(e.target.value)} style={{ ...input, width: "100%" }} disabled={loadingLista}>
              <option value="">{loadingLista ? "Cargando…" : "— Selecciona un presupuesto —"}</option>
              {presupuestos.map(p => (
                <option key={p.id} value={p.id}>
                  v{p.version} · {p.nombre} · {estadoConfig(p.estado).label} · {p.sucursal ? p.sucursal.nombre : "Empresa"}
                </option>
              ))}
            </select>
            {!loadingLista && presupuestos.length === 0 && (
              <small style={{ color: "#94a3b8", display: "block", marginTop: 3 }}>
                No hay presupuestos aprobados/activos/cerrados para {anio}.
              </small>
            )}
          </div>
          {detalle && (
            <button type="button" className="btn btn-primary"
              onClick={cargarVentasReales}
              disabled={cargandoVentas || !detalle.periodos?.length}
              style={{ height: 36 }}>
              {cargandoVentas ? (
                <><Spinner size="sm" className="me-1" /> Cargando ventas…</>
              ) : (
                <><i className="mdi mdi-cloud-download me-1" /> Cargar ventas reales</>
              )}
            </button>
          )}
        </div>

        {detalle && (
          <div style={{
            marginTop: 12, padding: "10px 14px",
            background: estadoCfg.bg, border: `1px solid ${estadoCfg.color}40`,
            borderRadius: 8, fontSize: 13,
          }}>
            <i className={`mdi ${estadoCfg.icon} me-2`} style={{ color: estadoCfg.color }} />
            <strong style={{ color: estadoCfg.color }}>{detalle.nombre}</strong>{" "}
            <span style={{ color: "#475569" }}>
              · {periodicidadLabel(detalle.periodicidad)} · {sucursalNombre} ·{" "}
              {formatFecha(detalle.fechaInicio)} — {formatFecha(detalle.fechaFin)} ·{" "}
              v{detalle.version} · {detalle.periodos?.length || 0} periodos
            </span>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10 }}>
          <i className="mdi mdi-alert-circle me-1" /> {error}
        </div>
      )}

      {/* Empty state si no hay presupuesto seleccionado */}
      {!presupuestoId && !loadingDetalle && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-chart-bar-stacked" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>Selecciona un presupuesto</h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Elige un presupuesto aprobado/activo/cerrado del dropdown para comparar sus objetivos contra las ventas reales del facturador.
          </p>
        </div>
      )}

      {/* Loading detalle */}
      {loadingDetalle && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2 text-muted">Cargando presupuesto…</div>
        </div>
      )}

      {/* Sin ventas cargadas aún */}
      {detalle && !cargandoVentas && Object.keys(ventasPorPeriodo).length === 0 && !error && (
        <div style={{ background: "linear-gradient(135deg, #eff6ff, #ffffff)", border: "1px solid #bfdbfe", borderRadius: 14, padding: "32px 40px", textAlign: "center" }}>
          <i className="mdi mdi-cloud-download-outline" style={{ fontSize: 48, color: "#3b82f6" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Presupuesto cargado · {detalle.periodos?.length || 0} periodos
          </h5>
          <p style={{ color: "#475569", maxWidth: 480, margin: "8px auto 16px", fontSize: 13 }}>
            Pulsá <strong>Cargar ventas reales</strong> para consultar la facturación de cada periodo y calcular el % de cumplimiento.
          </p>
        </div>
      )}

      {/* KPIs + Charts + Tabla */}
      {detalle && totales && totales.conReal > 0 && (
        <>
          {/* KPIs globales */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <KpiCard icon="mdi-target" label="Objetivo total" accent="#3b82f6"
              value={formatCurrency(totales.objetivo, moneda)} />
            <KpiCard icon="mdi-cash-multiple" label="Real total" accent="#10b981"
              value={formatCurrency(totales.real, moneda)} />
            <KpiCard
              icon="mdi-percent-outline"
              label="% Cumplimiento"
              value={formatPercent(totales.cumplimiento, 2)}
              accent={cumplimientoConfig(totales.cumplimiento).color}
              sub={cumplimientoConfig(totales.cumplimiento).label}
            />
            <KpiCard icon="mdi-file-document-outline" label="Documentos reales" accent="#8b5cf6"
              value={formatNumber(totales.documentos)} />
            <KpiCard
              icon="mdi-check-circle-outline"
              label="Periodos OK"
              value={`${totales.periodosCumplidos} de ${totales.conReal}`}
              accent="#16a34a"
              sub={`${totales.periodosEnRiesgo} en riesgo · ${totales.periodosBajos} bajos`}
            />
          </div>

          {/* Tendencia: objetivo vs real */}
          <SectionCard title="Tendencia: objetivo vs real" icon="mdi-chart-line">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v, moneda).replace("COP", "").trim()} />
                <Tooltip formatter={v => formatCurrency(v, moneda)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="objetivo" name="Objetivo" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} strokeDasharray="6 4" />
                <Line type="monotone" dataKey="real"     name="Real"     stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </SectionCard>

          {/* % Cumplimiento por periodo (barras coloreadas) */}
          <SectionCard title="% Cumplimiento por periodo" icon="mdi-chart-bar">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData.map(d => ({ etiqueta: d.etiqueta, pct: capPct(d.cumplimientoPct), realPct: d.cumplimientoPct }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} domain={[0, 150]} />
                <Tooltip formatter={(v, name, p) => [`${(p.payload.realPct || 0).toFixed(2)}%`, "% Cumplimiento"]} />
                <ReferenceLine y={95} stroke="#16a34a" strokeDasharray="4 4" label={{ value: "Cumplido 95%", position: "right", fontSize: 10, fill: "#16a34a" }} />
                <ReferenceLine y={80} stroke="#ca8a04" strokeDasharray="4 4" label={{ value: "Riesgo 80%", position: "right", fontSize: 10, fill: "#ca8a04" }} />
                <Bar dataKey="pct" radius={[6, 6, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={cumplimientoConfig(d.cumplimientoPct).color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>

          {/* Tabla detalle */}
          <SectionCard title="Detalle por periodo" icon="mdi-table-large">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Periodo</th>
                    <th style={th}>Vigencia</th>
                    {muestraMonto(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Objetivo</th>}
                    {muestraMonto(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Real</th>}
                    {muestraUnidades(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Unidades obj.</th>}
                    <th style={{ ...th, textAlign: "right" }}>Documentos</th>
                    <th style={{ ...th, textAlign: "center", width: 200 }}>% Cumplimiento</th>
                    <th style={{ ...th, textAlign: "center" }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filasComparativo.map((f, i) => {
                    const cfg = cumplimientoConfig(f.cumplMonto)
                    return (
                      <tr key={f.periodo.id} style={{ background: i % 2 ? "#fafbfc" : "#fff" }}>
                        <td style={td}>{f.periodo.numeroPeriodo}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{f.periodo.etiqueta}</td>
                        <td style={{ ...td, fontSize: 11, color: "#64748b" }}>
                          {formatFecha(f.periodo.fechaInicio)} — {formatFecha(f.periodo.fechaFin)}
                        </td>
                        {muestraMonto(tipoMedicion) && (
                          <td style={{ ...td, textAlign: "right" }}>{formatCurrency(f.objetivoMonto, moneda)}</td>
                        )}
                        {muestraMonto(tipoMedicion) && (
                          <td style={{ ...td, textAlign: "right", color: "#10b981", fontWeight: 700 }}>
                            {f.loading ? <Spinner size="sm" /> :
                              f.error ? <span style={{ color: "#dc2626", fontSize: 11 }}>error</span> :
                                f.realMonto != null ? formatCurrency(f.realMonto, moneda) :
                                  <span style={{ color: "#cbd5e1" }}>—</span>}
                          </td>
                        )}
                        {muestraUnidades(tipoMedicion) && (
                          <td style={{ ...td, textAlign: "right" }}>{formatNumber(f.objetivoUnidades, 0)}</td>
                        )}
                        <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                          {f.realDocumentos != null ? formatNumber(f.realDocumentos) : <span style={{ color: "#cbd5e1" }}>—</span>}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          {f.cumplMonto != null ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 999, height: 8, overflow: "hidden" }}>
                                <div style={{
                                  width: `${capPct(f.cumplMonto)}%`,
                                  height: "100%", background: cfg.color, transition: "width .3s",
                                }} />
                              </div>
                              <strong style={{ fontSize: 12, color: cfg.color, minWidth: 60, textAlign: "right" }}>
                                {f.cumplMonto.toFixed(1)}%
                              </strong>
                            </div>
                          ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <span style={{
                            padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                            background: cfg.bg, color: cfg.color,
                            display: "inline-flex", alignItems: "center", gap: 4,
                          }}>
                            <i className={`mdi ${cfg.icon}`} />{cfg.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                    <td style={td} colSpan={3}>TOTAL</td>
                    {muestraMonto(tipoMedicion) && (
                      <td style={{ ...td, textAlign: "right", color: "#3b82f6" }}>{formatCurrency(totales.objetivo, moneda)}</td>
                    )}
                    {muestraMonto(tipoMedicion) && (
                      <td style={{ ...td, textAlign: "right", color: "#10b981" }}>{formatCurrency(totales.real, moneda)}</td>
                    )}
                    {muestraUnidades(tipoMedicion) && <td style={{ ...td, textAlign: "right" }}>—</td>}
                    <td style={{ ...td, textAlign: "right" }}>{formatNumber(totales.documentos)}</td>
                    <td style={{ ...td, textAlign: "center", color: cumplimientoConfig(totales.cumplimiento).color, fontSize: 14 }}>
                      {formatPercent(totales.cumplimiento, 2)}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{
                        padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                        background: cumplimientoConfig(totales.cumplimiento).bg,
                        color: cumplimientoConfig(totales.cumplimiento).color,
                      }}>
                        {cumplimientoConfig(totales.cumplimiento).label}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {presupuestoSeleccionado && (
              <div style={{ marginTop: 12, fontSize: 11, color: "#94a3b8", textAlign: "right" }}>
                <i className="mdi mdi-information-outline me-1" />
                Ventas reales obtenidas de servicesV2/analytics/dashboard por cada periodo.
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  )
}

// ───── UI helpers ─────
const KpiCard = ({ icon, label, value, accent, sub }) => (
  <div style={{
    flex: "1 1 200px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "16px 20px",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)
KpiCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const SectionCard = ({ title, icon, children }) => (
  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
    <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
      {icon && <i className={`mdi ${icon} me-2 text-primary`} />}
      {title}
    </h6>
    {children}
  </div>
)
SectionCard.propTypes = { title: PropTypes.node, icon: PropTypes.string, children: PropTypes.node }

const labelStyle = { fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }
const input = { fontSize: 13, padding: "7px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 36, minWidth: 120 }
const th = { background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }
const td = { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#0f172a", verticalAlign: "middle" }

ComparativoVentas.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ComparativoVentas)
